import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // Sticky behavior with backdrop blur
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`header-professional ${isScrolled ? 'scrolled' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(10, 10, 10, 0.78)',
        backdropFilter: 'blur(14px) saturate(160%)',
        WebkitBackdropFilter: 'blur(14px) saturate(160%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div className="container-professional">
        <div
          className="flex justify-between items-center transition-[height] duration-300 ease-out"
          style={{ height: isScrolled ? '72px' : '88px' }}
        >
          
          <Link 
            to="/" 
            className="flex items-center rounded-md transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background hover:opacity-85"
            aria-label="AffinityAim Home"
          >
            <img
              src="/lovable-uploads/564b590b-a081-470a-ae9e-f75de8d420db.png"
              alt="AffinityAim - Custom Business Software"
              width="1500"
              height="1500"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-auto transition-[height] duration-300 ease-out"
              style={{
                height: isScrolled ? '64px' : '88px',
                imageRendering: 'auto',
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <nav className="flex items-center gap-8" role="navigation" aria-label="Main navigation">
              <Link 
                to="/services" 
                className={`text-[13.5px] font-medium tracking-[-0.005em] transition-colors duration-200 ${
                  isActive('/services') ? 'text-white' : 'text-white/65 hover:text-white'
                }`}
              >
                What we build
              </Link>
              <Link 
                to="/#how-it-works" 
                className="text-[13.5px] font-medium tracking-[-0.005em] transition-colors duration-200 text-white/65 hover:text-white"
              >
                How it works
              </Link>
              <Link 
                to="/about" 
                className={`text-[13.5px] font-medium tracking-[-0.005em] transition-colors duration-200 ${
                  isActive('/about') ? 'text-white' : 'text-white/65 hover:text-white'
                }`}
              >
                About
              </Link>
              <Link 
                to="/blog" 
                className={`text-[13.5px] font-medium tracking-[-0.005em] transition-colors duration-200 ${
                  isActive('/blog') ? 'text-white' : 'text-white/65 hover:text-white'
                }`}
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                className={`text-[13.5px] font-medium tracking-[-0.005em] transition-colors duration-200 ${
                  isActive('/contact') ? 'text-white' : 'text-white/65 hover:text-white'
                }`}
              >
                Contact
              </Link>
            </nav>
            <Link to="/contact">
              <button
                className="h-10 px-5 text-[13.5px] bg-primary hover:bg-[#1aeaff] text-[#001216] font-semibold rounded-md transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
              >
                Book a free call
              </button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary text-white/80 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            id="mobile-menu"
            className="md:hidden py-4 border-t border-white/[0.07]"
          >
            <nav className="flex flex-col" role="navigation" aria-label="Mobile navigation">
              {[
                { to: '/services', label: 'What we build' },
                { to: '/#how-it-works', label: 'How it works' },
                { to: '/about', label: 'About' },
                { to: '/blog', label: 'Blog' },
                { to: '/contact', label: 'Contact' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-white/75 hover:text-white transition-colors py-3.5 text-base font-medium min-h-[44px] flex items-center border-b border-white/[0.05]"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setIsOpen(false)} className="pt-5">
                <button
                  className="w-full h-12 bg-primary hover:bg-[#1aeaff] text-[#001216] font-semibold rounded-xl transition-colors text-base"
                >
                  Book a free call
                </button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
